import React, { useState } from "react";
import AskBar from "./AskBar.jsx";
import NoticeBar from "./NoticeBar.jsx";

/**
 * Composer — one bar on the person's page, two things it can do (Nov 1).
 *
 * Ask and "I noticed" used to be two bars, each with its own prompt and
 * captions. Now there is one place, and a switch above it: Ask | Note. Ask is
 * the default. The switch is chosen, never guessed from the text — dictated
 * questions have no question mark, and a note mistaken for a question would
 * be recorded as a question, with her name, and not saved.
 *
 * Someone not on the team (a contributor, a share-link visitor) gets Ask
 * alone, with no switch.
 *
 * The ⓘ (About this page) sits at the end of the switch row (Nov 2): the
 * sheet explains who sees questions and notes, so its help lives by the bar.
 */
export default function Composer({ clientId, clientName, poss = "their", team = false,
                                   shiftSharing = "off", onFirstUse, onNoteSaved, onInfo = null }) {
  const [mode, setMode] = useState("ask");
  const [switched, setSwitched] = useState(false);
  function pick(m) { setMode(m); setSwitched(true); }
  return (
    <section style={s.wrap} aria-label={team ? "Ask or note" : "Ask"}>
      {(team || onInfo) && (
        <div style={s.head}>
          {team ? (
            <div role="group" aria-label="Ask or note" style={s.switch}>
              {[["ask", "Ask"], ["note", "Note"]].map(([m, label]) => (
                <button key={m} type="button" aria-pressed={mode === m} onClick={() => pick(m)}
                        style={{ ...s.opt, ...(mode === m ? s.optOn : {}) }}>{label}</button>
              ))}
            </div>
          ) : <span style={s.headLabel}>Ask about {clientName}</span>}
          {onInfo && (
            <button type="button" aria-label="About this page" onClick={onInfo} style={s.info}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
              </svg>
            </button>
          )}
        </div>
      )}
      {mode === "ask" || !team ? (
        <AskBar clientId={clientId} clientName={clientName} pronoun={poss} onFirstUse={onFirstUse} />
      ) : (
        <NoticeBar clientId={clientId} shiftSharing={shiftSharing}
                   onFirstUse={onFirstUse} onSaved={onNoteSaved} autoFocus={switched} />
      )}
    </section>
  );
}

const s = {
  wrap:   { display: "flex", flexDirection: "column", gap: 8 },
  head:   { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  headLabel: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)", marginLeft: 4 },
  info:   { width: 40, height: 40, border: 0, background: "transparent", color: "var(--color-text-tertiary)", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", borderRadius: 999, flexShrink: 0 },
  switch: { display: "inline-flex", alignSelf: "flex-start", background: "var(--color-bg-inset)", borderRadius: 999, padding: 3 },
  opt:    { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", minHeight: 36, padding: "0 18px", border: 0, borderRadius: 999, background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer" },
  optOn:  { background: "var(--color-surface)", color: "var(--color-text-primary)", fontWeight: "var(--weight-semibold)", boxShadow: "0 1px 2px rgba(26,25,21,0.12)" },
};
